import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../lib/api';
import { QrCode, Users, CheckCircle, Clock, Calendar, MapPin, ChevronRight, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import ProducerLayout from '../../components/producer/ProducerLayout';

const ValidatorDashboardPage = () => {
  const [validator, setValidator] = useState(null);
  const [loading, setLoading] = useState(true);
  const [eventStats, setEventStats] = useState({});
  const [refreshing, setRefreshing] = useState(false);

  const loadValidatorProfile = async () => {
    try {
      const response = await api.get('/producers/validators/me');
      setValidator(response.data);
      
      // Load stats for each assigned event
      if (response.data.assigned_events?.length > 0) {
        loadAllEventStats(response.data.assigned_events);
      }
    } catch (error) {
      console.error('Error loading validator profile:', error);
      toast.error('Error al cargar el perfil');
    } finally {
      setLoading(false);
    }
  };

  const loadAllEventStats = async (events) => {
    const stats = {};
    for (const event of events) {
      try {
        const response = await api.get(`/producers/validators/events/${event.id}/scanner-stats`);
        stats[event.id] = response.data;
      } catch (error) {
        console.error(`Error loading stats for event ${event.id}:`, error);
        stats[event.id] = { total: 0, checked_in: 0, pending: 0 };
      }
    }
    setEventStats(stats);
  };

  const refreshStats = async () => {
    if (!validator?.assigned_events?.length) return;
    setRefreshing(true);
    await loadAllEventStats(validator.assigned_events);
    setRefreshing(false);
    toast.success('Estadísticas actualizadas');
  };

  useEffect(() => {
    loadValidatorProfile();
    
    // Auto-refresh every 30 seconds
    const interval = setInterval(() => {
      if (validator?.assigned_events?.length > 0) {
        loadAllEventStats(validator.assigned_events);
      }
    }, 30000);
    
    return () => clearInterval(interval);
  }, []);

  // Calculate totals
  const totalStats = Object.values(eventStats).reduce((acc, stats) => ({
    total: acc.total + (stats.total || 0),
    checked_in: acc.checked_in + (stats.checked_in || 0),
    pending: acc.pending + (stats.pending || 0)
  }), { total: 0, checked_in: 0, pending: 0 });

  if (loading) {
    return (
      <ProducerLayout>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 text-violet-500 animate-spin" />
        </div>
      </ProducerLayout>
    );
  }

  return (
    <ProducerLayout>
      <div className="p-4 lg:p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">
              Hola, {validator?.name?.split(' ')[0] || 'Validador'}
            </h1>
            <p className="text-zinc-400 text-sm mt-1">
              {validator?.producer_name && `Validador de ${validator.producer_name}`}
            </p>
          </div>
          <button
            onClick={refreshStats}
            disabled={refreshing}
            className="flex items-center gap-2 px-4 py-2 bg-zinc-800 hover:bg-zinc-700 rounded-lg text-zinc-300 transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            <span className="hidden sm:inline">Actualizar</span>
          </button>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 text-center">
            <Users className="w-6 h-6 mx-auto text-violet-500 mb-2" />
            <p className="text-2xl font-bold text-white">{totalStats.total}</p>
            <p className="text-xs text-zinc-500">Total Entradas</p>
          </div>
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 text-center">
            <CheckCircle className="w-6 h-6 mx-auto text-green-500 mb-2" />
            <p className="text-2xl font-bold text-green-400">{totalStats.checked_in}</p>
            <p className="text-xs text-zinc-500">Ingresados</p>
          </div>
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 text-center">
            <Clock className="w-6 h-6 mx-auto text-amber-500 mb-2" />
            <p className="text-2xl font-bold text-amber-400">{totalStats.pending}</p>
            <p className="text-xs text-zinc-500">Pendientes</p>
          </div>
        </div>

        {/* Quick Action - Go to Scanner */}
        <Link
          to="/producer/scanner"
          className="flex items-center justify-center gap-3 w-full py-4 bg-violet-500 hover:bg-[#C4A030] text-black font-semibold rounded-xl transition-colors"
          data-testid="go-to-scanner-btn"
        >
          <QrCode className="w-6 h-6" />
          <span>Escanear Entradas</span>
        </Link>

        {/* Assigned Events */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-white">
              {validator?.has_all_events_access ? 'Todos los Eventos' : 'Eventos Asignados'}
            </h2>
            {validator?.has_all_events_access && (
              <span className="px-2 py-1 text-xs bg-violet-500/20 text-violet-500 rounded-full">
                Acceso Total
              </span>
            )}
          </div>

          {!validator?.assigned_events?.length ? (
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-8 text-center">
              <Calendar className="w-12 h-12 mx-auto text-zinc-600 mb-3" />
              <p className="text-zinc-400">No tienes eventos asignados</p>
              <p className="text-zinc-500 text-sm mt-1">
                Contacta con tu productora para que te asigne eventos
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {validator.assigned_events.map(event => {
                const stats = eventStats[event.id] || { total: 0, checked_in: 0, pending: 0 };
                const progress = stats.total > 0 ? (stats.checked_in / stats.total) * 100 : 0;
                
                return (
                  <Link
                    key={event.id}
                    to={`/producer/scanner?event=${event.id}`}
                    className="block bg-zinc-900 border border-zinc-800 rounded-xl p-4 hover:border-zinc-700 transition-colors"
                    data-testid={`event-card-${event.id}`}
                  >
                    <div className="flex items-start gap-4">
                      {/* Event Image */}
                      {event.image_url ? (
                        <img
                          src={event.image_url}
                          alt={event.title}
                          className="w-16 h-16 rounded-lg object-cover flex-shrink-0"
                        />
                      ) : (
                        <div className="w-16 h-16 rounded-lg bg-zinc-800 flex items-center justify-center flex-shrink-0">
                          <Calendar className="w-6 h-6 text-zinc-600" />
                        </div>
                      )}
                      
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-white truncate">{event.title}</h3>
                        
                        <div className="flex items-center gap-3 mt-1 text-xs text-zinc-500">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {event.date ? new Date(event.date).toLocaleDateString('es-AR', {
                              day: 'numeric',
                              month: 'short',
                              timeZone: 'America/Argentina/Buenos_Aires'
                            }) : 'Sin fecha'}
                          </span>
                          {event.location && (
                            <span className="flex items-center gap-1">
                              <MapPin className="w-3 h-3" />
                              {event.location.split(',')[0]}
                            </span>
                          )}
                        </div>
                        
                        {/* Progress Bar */}
                        <div className="mt-3">
                          <div className="flex items-center justify-between text-xs mb-1">
                            <span className="text-zinc-400">
                              {stats.checked_in} / {stats.total} ingresados
                            </span>
                            <span className="text-zinc-500">{Math.round(progress)}%</span>
                          </div>
                          <div className="h-1.5 bg-zinc-800 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-violet-500 rounded-full transition-all duration-500"
                              style={{ width: `${progress}%` }}
                            />
                          </div>
                        </div>
                      </div>
                      
                      <ChevronRight className="w-5 h-5 text-zinc-600 flex-shrink-0" />
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>

        {/* Help Section */}
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
          <h3 className="font-medium text-zinc-300 mb-2">Ayuda Rápida</h3>
          <ul className="space-y-2 text-sm text-zinc-500">
            <li className="flex items-start gap-2">
              <span className="text-violet-500">•</span>
              Escanea el código QR del ticket para validar la entrada
            </li>
            <li className="flex items-start gap-2">
              <span className="text-violet-500">•</span>
              También puedes buscar por DNI o código de ticket manualmente
            </li>
            <li className="flex items-start gap-2">
              <span className="text-violet-500">•</span>
              Las estadísticas se actualizan automáticamente cada 30 segundos
            </li>
          </ul>
        </div>
      </div>
    </ProducerLayout>
  );
};

export default ValidatorDashboardPage;
